import { useQuery } from '@tanstack/react-query';
import { useUserData } from '@/src/listings/hooks/useUserData';
import { releaseFetchWithOverrideRecovery } from '@/lib/release/pageSourceOverride';

const STALE_MS = 5 * 60 * 1000;

type SubscriptionResponse = {
  isSubscribed?: boolean;
  hasEarlyAccessPurchaseWindows?: boolean;
};

export type UserSubscriptionLookup = {
  isSubscribed: boolean;
  hasEarlyAccessPurchaseWindows: boolean;
  isSubscriptionReady: boolean;
};

async function fetchUserSubscription(): Promise<SubscriptionResponse> {
  const response = await releaseFetchWithOverrideRecovery('/e/api/user/subscription', { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`User subscription request failed with status ${response.status}`);
  }

  return (await response.json()) as SubscriptionResponse;
}

export function useUserSubscription(): UserSubscriptionLookup {
  const { userId, isLoading: isUserDataLoading } = useUserData();
  // Membership is fetched independent of the Orion flag: an existing
  // subscriber keeps their entitlement even when the experiment is off.
  const enabled = !!userId;
  const { data, isFetched } = useQuery({
    queryKey: ['user-subscription', userId ?? 'anonymous'],
    queryFn: fetchUserSubscription,
    enabled,
    staleTime: STALE_MS,
    retry: false,
  });

  if (!isUserDataLoading && !userId) {
    return {
      isSubscribed: false,
      hasEarlyAccessPurchaseWindows: false,
      isSubscriptionReady: true,
    };
  }

  return {
    isSubscribed: data?.isSubscribed ?? false,
    hasEarlyAccessPurchaseWindows: data?.hasEarlyAccessPurchaseWindows ?? false,
    isSubscriptionReady: !isUserDataLoading && isFetched,
  };
}
